import { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { parseDriveUrl } from '../../utils/driveUrl';
import { fetchDriveMetadata } from '../../services/driveApi';
import { uploadFile } from '../../services/uploadApi';
import { useAuth } from '../../hooks/useAuth';
import type { AddCardFn } from '../../types/addCard';

const ACCEPT = '.pdf,.html,.htm,.png,.jpg,.jpeg,.gif,.webp,.svg';

interface Props {
  onSave: AddCardFn;
  blockId?: string;
}

export default function AddCardInline({ onSave, blockId }: Props) {
  const { getGoogleAccessToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  // Pending upload result (set after a file is uploaded, before the card is saved)
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<{ url: string; thumbnailUrl: string | null; fileName: string } | null>(null);

  const handleUrlBlur = async () => {
    if (!url.trim()) return;

    const parsed = parseDriveUrl(url.trim());
    if (!parsed) return;

    const token = await getGoogleAccessToken();
    if (!token) return;

    setFetching(true);
    try {
      const result = await fetchDriveMetadata(parsed.docId, token);
      if (result.ok) {
        if (!title) setTitle(result.data.name);
        if (!description && result.data.description) setDescription(result.data.description);
      }
    } catch {
      // Silent
    } finally {
      setFetching(false);
    }
  };

  const reset = () => {
    setEditing(false);
    setTitle('');
    setUrl('');
    setDescription('');
    setUploaded(null);
    setUploadError(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later
    e.target.value = '';
    if (!file) return;

    setUploadError(null);
    setUploading(true);
    try {
      const result = await uploadFile(file);
      if (result.ok) {
        setUploaded({
          url: result.data.url,
          thumbnailUrl: result.data.thumbnailUrl,
          fileName: result.data.fileName,
        });
        // Prefill title from filename if empty
        if (!title) setTitle(result.data.fileName.replace(/\.[^.]+$/, ''));
      } else {
        setUploadError(
          result.error.kind === 'unauthorized'
            ? 'Session expired. Refresh and try again.'
            : result.error.message,
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const clearUpload = () => {
    setUploaded(null);
    setUploadError(null);
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      // An uploaded file becomes the card's link; its thumbnail is stored on the card.
      const finalUrl = uploaded ? uploaded.url : url.trim();
      const extra = uploaded
        ? { thumbnailUrl: uploaded.thumbnailUrl ?? undefined, pageType: 'upload' as const }
        : undefined;
      await onSave(title.trim(), finalUrl, description.trim(), blockId, extra);
      reset();
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') reset();
  };

  if (!editing) {
    return (
      <Card
        onClick={() => setEditing(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 3,
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: 'divider',
          bgcolor: 'transparent',
          boxShadow: 'none',
          color: 'text.secondary',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            boxShadow: 'none',
          },
        }}
      >
        <AddIcon sx={{ fontSize: 20 }} />
        <Typography variant="body2" fontWeight={500}>Add</Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        size="small"
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        fullWidth
      />
      <TextField
        size="small"
        placeholder="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={handleKeyDown}
        fullWidth
        multiline
        minRows={2}
      />
      {uploaded ? (
        <Chip
          icon={<UploadFileIcon sx={{ fontSize: 16 }} />}
          label={uploaded.fileName}
          onDelete={clearUpload}
          deleteIcon={<CloseIcon />}
          variant="outlined"
          sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}
        />
      ) : (
        <TextField
          size="small"
          placeholder="Link (optional - Google Doc, webpage, etc.)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={handleUrlBlur}
          onKeyDown={handleKeyDown}
          fullWidth
          slotProps={{
            input: {
              endAdornment: fetching ? <CircularProgress size={16} /> : null,
            },
          }}
        />
      )}

      {!uploaded && (
        <>
          <Divider sx={{ '&::before, &::after': { borderColor: 'divider' } }}>
            <Typography variant="caption" color="text.disabled">or</Typography>
          </Divider>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={handleFileSelect}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{ alignSelf: 'flex-start' }}
          >
            {uploading ? 'Uploading…' : 'Upload a file (PDF, HTML, image)'}
          </Button>
        </>
      )}

      {uploadError && (
        <Typography variant="caption" color="error">{uploadError}</Typography>
      )}

      <Box display="flex" gap={1} justifyContent="flex-end">
        <Button size="small" onClick={reset} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving || uploading || !title.trim()}
        >
          {saving ? <CircularProgress size={16} /> : 'Add'}
        </Button>
      </Box>
    </Card>
  );
}
